export const RecordOptionFragment = gql`
  fragment RecordOptionFragment on Record {
    id
    currentUserHasAccess
    uuid
    tags
    identifier
    issuedIdentifier
    isRenewal
    expirationDate
    formattedName
    status {
      ...RecordTypeStatusFragment
    }
    tags
    isDraft
    renewedByRecord {
      id
    }
    recordSettingsDerived {
      shouldExposeTagsToCitizens
    }
    recordTemplate {
      id
      uuid
      name
      recordType {
        id
        displayName
      }
    }
    parentRecord {
      id
    }
  }
`

export const RelatedRecordFragment = gql`
  fragment RelatedRecordFragment on RelatedRecord {
    record {
      ...RecordOptionFragment
    }
    relationshipType
  }
`

export const RecordFragment = gql`
  fragment RecordFragment on Record {
    id
    createdAt
    expirationDate
    formattedName
    hasPlanReview
    identifier
    displayIdentifier
    issuedAt
    issuedIdentifier
    isApplicantAnonymized
    isTest
    closedAt
    canceledAt
    isRecordClosedOrCanceled
    showInspectionsTab
    submittedAt
    tags
    alertSourceCandidates {
      ...AlertSourceCandidateFragment
    }
    allFieldGroupsVersions {
      ...RecordFieldGroupVersionFragment
    }
    recordSettingsDerived {
      shouldExposeTagsToCitizens
      shouldPromoteTagsForStaff
    }
    currentFormFieldGroupVersion {
      id
      fieldGroups {
        ...FieldGroupFragment
      }
    }
    unpaidRecordTaskPaymentsCount
    uuid
    hasBeenRenewed
    isRenewal
    canceledByUser {
      ...UserDataFragment
    }
    closedByUser {
      ...UserDataFragment
    }
    relatedAlerts {
      ...AlertFragment
    }
    relatedRecords {
      ...RelatedRecordFragment
    }
    accesses {
      id
      user {
        ...UserDataFragment
      }
    }
    currentCaseHistory {
      id
      violationType {
        id
        name
      }
    }
    latestRenewalInfo {
      ...RecordLatestRenewalInfoFragment
    }
    organization {
      id
      uuid
      slug
      displayName
      timeZone
      entityLayers {
        id
      }
    }
    status {
      id
      name
      type
    }
    resolvedAt
    isResolved
    isViolation
    isDraft
    importedAt
    recordCollections {
      id
      name
      uuid
    }
    recordOnMeetings {
      ...RecordOnMeetingFragment
    }
    mostRecentPlansSet {
      id
    }
    currentRecordTaskGroups {
      ...RecordTaskGroupBaseFragment
    }
    contactsFieldGroup {
      fields {
        id
      }
    }
    addressField {
      ...AddressFieldFragment
    }
    mainBackwardsCompatibleLocationInformation {
      ...BackwardsCompatibleLocationInformationFragment
    }
    ownerUser {
      ...UserDataFragment
    }
    ownerUserIsSelectedByAutomation
    applicantUser {
      ...RecordApplicantUserFragment
    }
    applicantCompanyOrRequestedToJoinCompany {
      ...CompanyFragment
    }
    recordTaskInspectionGroups {
      id
      inspections {
        id
      }
    }
    generatedDocuments {
      ...RecordGeneratedDocumentFragment
    }
    recordTemplate {
      id
      name
      recordTypeId
      icon
      applicantType
      recordType {
        ...RecordComponentRecordTypeFragment
      }
      recordSettingsDerived {
        ...RecordSettingsFragment
      }
    }
    currentFormFieldGroupVersion {
      id
      fieldGroups {
        ...FieldGroupFragment
      }
    }
    recordTaskGroups {
      ...RecordComponentRecordTaskGroupFragment
    }
    createdByUser {
      ...UserDataFragmentWithCompany
    }
    workflows {
      ...WorkflowOptionFragment
    }
    emailAttachments {
      ...RecordEmailFileFragment
    }
    missingTemplateFields {
      id
    }
    autoCheckFeedbackItems {
      ...AutoCheckFeedbackItemFragment
    }
  }
  fragment RecordApplicantUserFragment on User {
    ...UserDataFragment
    uuid
  }
  fragment RecordComponentRecordTypeFragment on RecordType {
    id
    name
    recordName
    category
    allowFees
    areMeetingsEnabled
    addressEntryType
    organization {
      city
      uuid
      slug
      id
      timeZone
    }
  }
  fragment RecordComponentRecordTaskInspectionGroupFragment on RecordTaskInspectionGroup {
    alwaysAvailableToApplicants
    ...RecordTaskInspectionGroupFragment
    recordTaskGroup {
      id
    }
    inspections {
      id
      attempts {
        ...RecordTaskInspectionAttemptDetailsFragment
      }
    }
  }
  fragment RecordComponentRecordTaskGroupFragment on RecordTaskGroup {
    id
    status
    type
    isLocked
    recordTaskTemplateGroup {
      identifier
    }
    recordTasks {
      ...RecordComponentRecordTaskFragment
    }
  }
  fragment RecordComponentRecordTaskFragment on RecordTask {
    id
    status
    type
    name
    ownerUser {
      ...UserDataFragment
    }
    fieldGroup {
      ...FieldGroupFragment
    }
    checklistItems {
      id
      completedAt
    }
    createdRecord {
      id
      uuid
    }
    requestedRecordTemplate {
      ...RecordTemplateOptionFragment
    }
    recordTaskTemplate {
      addFilesToPlanReview
    }
    revisionOfPlansSet {
      id
    }
  }
  fragment InspectionRecordTaskGroupFragment on RecordTaskGroup {
    id
    isLocked
    name
    status
    type
    ordinal
    recordTaskTemplateGroup {
      identifier
    }
    recordTasks {
      ...RecordComponentRecordTaskFragment
    }
    workflow {
      id
      completedAt
      createdAt
      status
      workflowTemplate {
        name
      }
    }
  }
  fragment RecordGeneratedDocumentFragment on RecordGeneratedDocument {
    id
    uuid
    file {
      ...FileFragment
    }
    recordGeneratedDocumentType
    issuedByUser {
      ...UserDataFragment
    }
    createdByUser {
      ...UserDataFragment
    }
    documentTemplateId
    identifier
    validFrom
    validUntil
    createdAt
    isAutoGenerated
  }
  fragment RecordFieldGroupVersionFragment on RecordFieldGroupsVersion {
    id
    createdByUser {
      ...UserDataFragment
    }
    createdAt
    isMostRecentVersionForRecord
    recordInPlaceRenewal {
      id
    }
  }
  fragment RecordLatestRenewalInfoFragment on RecordLatestRenewalInfo {
    createdAt
    renewedByRecord {
      id
      uuid
      issuedIdentifier
    }
    renewedByUser {
      ...UserDataFragment
    }
  }
`

export const RecordUpdatedRecordTemplateHistoriesFragment = gql`
  fragment RecordUpdatedRecordTemplateHistoriesFragment on Record {
    recordTemplateUpdatedHistoryItemsDesc {
      ...RecordHistoryItemFragment
    }
  }
`

export const AlertsForRecordFragment = gql`
  fragment AlertsForRecordFragment on Record {
    id
    relatedAlerts {
      ...AlertFragment
    }
    alertSourceCandidates {
      ...AlertSourceCandidateFragment
    }
  }
`

export const RecordIdentifierFragment = gql`
  fragment RecordIdentifierFragment on RecordIdentifier {
    id
    prefix
    suffix
    year
  }
`

export const RecordIssuedIdentifierFragment = gql`
  fragment RecordIssuedIdentifierFragment on Record {
    id
    issuedIdentifier
    isTest
    recordIdentifier {
      ...RecordIdentifierFragment
    }
    recordTemplate {
      prefix
    }
  }
`
